import { Diagram, DiagramError, DiagramLink, DiagramNode } from '../diagram/diagram';
import {
  BackgroundImages,
  BasicProgress,
  CustomImage,
  GenericProgressStatus,
  menuEditionStatusFactory,
  Palette,
  Palettes,
  ProductGraphicPreferenceStatus,
  ProductMenuEditionStatus,
  ProductMenuValidationStep,
  ProductSession,
  SSOLink,
} from '../models';
import { CoursizStrategy } from './coursiz-edited';

export class CoursizModel implements Diagram {
  slug: string;
  name: string;
  local: string;
  context: CoursizContext;
  publishedDate?: string;
  nodes: DiagramNode[];
  links: DiagramLink[];
  params: CoursizParams;
  globalErrors: DiagramError[];
  strategy?: CoursizStrategy;
  editionInfo?: CoursizEditionInfo;

  constructor(coursizModel?: CoursizModel, slug?: string) {
    this.slug = slug ? slug : coursizModel?.slug;
    this.name = coursizModel?.name;
    this.local = coursizModel?.local;
    this.context = new CoursizContext(coursizModel?.context);
    this.editionInfo = new CoursizEditionInfo(coursizModel?.editionInfo);
    this.publishedDate = coursizModel?.publishedDate;
    this.nodes = coursizModel?.nodes ?? [];
    this.links = coursizModel?.links ?? [];
    this.params = new CoursizParams(coursizModel?.params);
    this.strategy = coursizModel?.strategy || CoursizStrategy.DEFAULT;
  }
}

export class CoursizContext {
  title: string;
  description: string;
  objectives: string;
  requirements: string;
  author: string;
  duration: string;
  tags: string[];

  constructor(context?: CoursizContext) {
    this.title = context?.title;
    this.description = context?.description;
    this.objectives = context?.objectives;
    this.requirements = context?.requirements;
    this.author = context?.author;
    this.duration = context?.duration;
    this.tags = (context?.tags || []).map(t => t);
  }
}

export class CoursizParams {
  status: ProductGraphicPreferenceStatus;
  colors: Palette;
  background: CustomImage;

  constructor(params?: CoursizParams) {
    this.colors = new Palette(params?.colors || Palettes[0]);
    this.background = new CustomImage(params?.background || BackgroundImages[0]);
    this.status = params?.status || ProductGraphicPreferenceStatus.DEFAULT;
  }
}

export class CoursizEditionInfo {
  paramsStatus: ProductMenuEditionStatus;
  contentStatus: ProductMenuEditionStatus;
  publicationStatus: ProductMenuEditionStatus;
  ssoLinks: SSOLink[];
  globalErrors: DiagramError[];

  /**
   * validationStep is for the stepper of the validation page
   * (in opposition of paramsStatus, contentStatus, publicationStatus)
   */
  validationStep: ProductMenuValidationStep;

  constructor(info?: CoursizEditionInfo) {
    this.paramsStatus = menuEditionStatusFactory(info?.paramsStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.contentStatus = menuEditionStatusFactory(info?.contentStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.publicationStatus = menuEditionStatusFactory(info?.publicationStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.ssoLinks = (info?.ssoLinks || []).map(link => new SSOLink(link));
    this.globalErrors = (info?.globalErrors ?? []).map(e => e);
    this.validationStep = info?.validationStep ?? ProductMenuValidationStep.SUMMARY;
  }
}

export interface CoursizProgress extends BasicProgress {
  status: GenericProgressStatus;
  products: CoursizNodeProgress[];
}

export interface CoursizNodeProgress {
  nbTimeDone?: number;
  slug: string;
  productSlug: string;
  status: GenericProgressStatus;
  productsSlugs?: CoursizNodeProgress[]; // for section
  score?: number; // for game and learniz containing games
  percentage?: number; // for learniz
  time?: number;
  eventDate?: string;
}

export type CoursizSession = ProductSession<CoursizModel, CoursizProgress>;

export interface CoursizSummary {
  numberOfGames: number;
  numberOfLearniz: number;
  numberOfClassiz: number;
  numberOfScorm: number;
  numberOfAttachment: number;
  numberOfSection: number;
}
